import { useCallback, useEffect, useRef, useState } from "react";
import logger from "../utils/logger";
import { generateRecordingFilename } from "../utils/downloadChunkedBlob";
import type { RecordingQuality } from "../context/RecorderContext";
import {
  db,
  putChunk,
  deleteChunks,
  deleteUpload,
  areAllChunksDone,
} from "../idb/chunk-db";
import { isUserAuthenticated } from "../services/user";
import {
  detectBrowser,
  getFallbackOptions,
  isRecordingSupported,
  getSupportedMimeType as getOptimalMimeType,
  BITRATES_BY_QUALITY,
  AUDIO_BITRATE,
  RECOMMENDED_TIMESLICE,
  VIMEO_TIMESLICE,
  VIDEO_KEYFRAME_INTERVAL_DURATION,
} from "../utils/browserCompatibility";
import { getUploadConfig } from "../utils/authUtils";
import {
  AudioContext,
  AudioWorkletNode,
  MediaStreamAudioSourceNode,
  GainNode,
} from "standardized-audio-context";

//@ts-ignore
import UploadWorker from "../worker/upload.worker";
import { useAtom } from "jotai";
import { showRecordingPreviewAtom, currentSceneAtom, activeProjectAtom, sceneListAtom } from "../atoms/sceneAtoms";
import SceneService from "../services/sceneService";
import { s3KeyStorage } from "../services/s3KeyStorage";
import { useMSTGRenderer } from "./useMSTGRenderer";
import { useSilentAudioLoop } from "./useSilentAudioLoop";
import { RECORDING_FLAGS } from "../constants/recordingFlags";
import { frameTimingCollector } from "../utils/PerformanceMonitor";
import { onboardingService } from "../services/onboardingService";

export interface UseMediaRecorderResult {
  isRecording: boolean;
  isPaused: boolean;
  getRecordingTime: () => number;
  startRecording: (overrideSceneId?: string) => Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  deleteRecording: () => void;
  retryUpload: (videoGuid: string, force?: boolean) => Promise<void>;
  currentVideoGuid: string | null;
  activeUploads: Set<string>;
  uploadInitializing: Set<string>;
  pendingChunks: Map<string, Array<{ chunkNo: number; size: number }>>;
  showDownloadOnlyOverlay: boolean;
  setShowDownloadOnlyOverlay: (show: boolean) => void;
  getSceneIdForVideoGuid: (videoGuid: string) => string | undefined;
  mstgWriteFrame: ((canvas: HTMLCanvasElement, frameNumber: number, renderDurationMs: number) => Promise<unknown>) | null;
  hasMSTG: boolean;
  isMSTGActive: () => boolean;
  requestCanvasFrame: () => boolean;
}

export const useMediaRecorder = (
  stageRef: React.RefObject<any> | null,
  micStream?: MediaStream | null,
  systemAudioStream?: MediaStream | null,
  recordingQuality?: RecordingQuality,
  canvasWidth?: number,
  canvasHeight?: number,
  canvasRatio?: string | undefined,
  setIsUploading?: (uploading: boolean) => void,
  onUploadError?: (videoGuid: string, error: string) => void,
  onChunkError?: (videoGuid: string, chunkNo: number) => void,
  onUploadComplete?: ({
    videoId,
    videoGuid,
  }: {
    videoId: string;
    videoGuid: string;
  }) => void,
  onRecordingStart?: () => void,
): UseMediaRecorderResult => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showDownloadOnlyOverlay, setShowDownloadOnlyOverlay] = useState(false);
  
  const [currentScene] = useAtom(currentSceneAtom);
  const [activeProject] = useAtom(activeProjectAtom);
  const [sceneList, setSceneList] = useAtom(sceneListAtom);
  const [, showRecordingPreview] = useAtom(showRecordingPreviewAtom);
  
  const recordingTimeRef = useRef(0);
  const recordingStartTimeRef = useRef<number | null>(null);
  const isRecordingRef = useRef(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const canvasStreamRef = useRef<MediaStream | null>(null);
  
  const pauseStartTimeRef = useRef<number | null>(null);
  const totalPausedDurationRef = useRef<number>(0);
  
  const currentVideoGuidRef = useRef<string | null>(null);
  const isStoppingRef = useRef<boolean>(false);
  
  const currentSceneRef = useRef(currentScene);
  const activeProjectRef = useRef(activeProject);
  const sceneListRef = useRef(sceneList);
  
  // CRITICAL FIX: Map videoGuid -> sceneId to avoid stale ref issues during retakes
  const videoGuidToSceneIdRef = useRef<Map<string, { sceneId: string; projectId: string }>>(new Map());
  
  useEffect(() => {
    currentSceneRef.current = currentScene;
    activeProjectRef.current = activeProject;
    sceneListRef.current = sceneList;
  }, [currentScene, activeProject, sceneList]);
  
  const browserInfo = useRef(detectBrowser());
  const fallbackOptions = useRef(getFallbackOptions());
  const isRecordingSupportedRef = useRef(isRecordingSupported());
  
  const uploadWorkersRef = useRef<Map<string, Worker>>(new Map());
  const uploadInitializedRef = useRef<Set<string>>(new Set());
  const uploadInitializingRef = useRef<Set<string>>(new Set());
  const pendingChunksRef = useRef<
    Map<string, Array<{ chunkNo: number; size: number }>>
  >(new Map());
  const activeUploadsRef = useRef<Set<string>>(new Set());
  const abortedVideoGuidsRef = useRef<Set<string>>(new Set());
  const uploadInitErrorsRef = useRef<Map<string, string>>(new Map());
  
  const audioCtxRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode<AudioContext> | null>(null);
  const micSourceNodeRef = useRef<MediaStreamAudioSourceNode<AudioContext> | null>(null);
  const systemAudioSourceNodeRef = useRef<MediaStreamAudioSourceNode<AudioContext> | null>(null);
  const audioCleanupRef = useRef<(() => void) | null>(null);
  const mixNodeRef = useRef<GainNode<AudioContext> | null>(null);
  const previousMicStreamRef = useRef<MediaStream | null | undefined>(null);
  
  const mstgRenderer = useMSTGRenderer();
  
  // Silent audio loop to prevent browser throttling when tab is hidden
  // Plays inaudible 20kHz tone to keep AudioContext active
  const silentAudioLoop = useSilentAudioLoop();
  
  // Track whether MSTG capture was started for current recording session
  // Only call stopMSTGCapture if startMSTGCapture was called
  const mstgCaptureStartedRef = useRef(false);
  
  // Track the canvas video track for on-demand frame capture
  // Used with captureStream(0) to call requestFrame() after each render
  const canvasVideoTrackRef = useRef<CanvasCaptureMediaStreamTrack | null>(null);

  const chunkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const nextChunkNoRef = useRef<number>(1);
  const chunkAccumulatorRef = useRef<Array<Blob>>([]);
  const accumulatedSizeRef = useRef<number>(0);

  const setupAudioPipeline = useCallback(async () => {
    if (!micStream && !systemAudioStream) {
      return null;
    }

    try {
      const ctx = new AudioContext();
      await ctx.resume();
      audioCtxRef.current = ctx;

      let micSrc: MediaStreamAudioSourceNode<AudioContext> | null = null;
      let systemAudioSrc: MediaStreamAudioSourceNode<AudioContext> | null = null;

      if (micStream) {
        micSrc = ctx.createMediaStreamSource(micStream);
        micSourceNodeRef.current = micSrc;
      }

      if (systemAudioStream) {
        systemAudioSrc = ctx.createMediaStreamSource(systemAudioStream);
        systemAudioSourceNodeRef.current = systemAudioSrc;
      }

      const mix = ctx.createGain();
      mix.gain.value = 1.25;
      mixNodeRef.current = mix;

      if (micSrc) micSrc.connect(mix);
      if (systemAudioSrc) systemAudioSrc.connect(mix);

      logger.info("✨ Audio pipeline enabled");

      audioCleanupRef.current = async () => {
        try {
          if (micSrc) micSrc.disconnect();
          if (systemAudioSrc) systemAudioSrc.disconnect();
          mix.disconnect();
          await ctx.close();
        } catch (error) {
          logger.warn("Error during audio cleanup:", error);
        } finally {
          audioCtxRef.current = null;
          workletNodeRef.current = null;
          micSourceNodeRef.current = null;
          systemAudioSourceNodeRef.current = null;
          mixNodeRef.current = null;
        }
      };

      return mix;
    } catch (error) {
      logger.error("Failed to setup audio pipeline:", error);
      return null;
    }
  }, [micStream, systemAudioStream]);

  const updateAudioPipeline = useCallback(async () => {
    if (!isRecordingRef.current || !audioCtxRef.current || !mixNodeRef.current) {
      return;
    }

    try {
      logger.info("🔄 Updating audio pipeline for new micStream");

      if (micSourceNodeRef.current) {
        micSourceNodeRef.current.disconnect();
        micSourceNodeRef.current = null;
      }

      if (micStream) {
        const newMicSrc = audioCtxRef.current.createMediaStreamSource(micStream);
        micSourceNodeRef.current = newMicSrc;
        newMicSrc.connect(mixNodeRef.current);
        logger.info("✅ Connected new micStream to audio pipeline");
      } else {
        logger.info("ℹ️ No micStream available, continuing without microphone");
      }
    } catch (error) {
      logger.error("Failed to update audio pipeline:", error);
    }
  }, [micStream]);

  /**
   * Wait for track readyState to become 'live' with timeout
   * MediaStreamTrackGenerator may have readyState='new' for a microtask after creation
   * Uses setTimeout instead of requestAnimationFrame to work in hidden tabs
   */
  const waitForTrackLive = useCallback(async (track: MediaStreamTrack, timeoutMs: number = 100): Promise<boolean> => {
    if (track.readyState === 'live') return true;
    
    return new Promise<boolean>((resolve) => {
      const startTime = performance.now();
      const POLL_INTERVAL_MS = 5; // 5ms polling interval (unthrottled even in hidden tabs)
      
      const check = () => {
        if (track.readyState === 'live') {
          resolve(true);
        } else if (performance.now() - startTime > timeoutMs) {
          resolve(false);
        } else {
          // Use setTimeout instead of rAF to avoid hidden-tab throttling
          setTimeout(check, POLL_INTERVAL_MS);
        }
      };
      
      // Start checking after a microtask
      Promise.resolve().then(check);
    });
  }, []);

  const getCanvasStream = useCallback(async () => {
    const fabricCanvas = stageRef?.current?.getCanvas?.();
    if (!fabricCanvas) {
      throw new Error("Canvas not available");
    }

    const domCanvas = fabricCanvas.lowerCanvasEl as HTMLCanvasElement;
    if (!domCanvas) {
      throw new Error("Canvas element not found");
    }

    let stream: MediaStream;
    
    if (RECORDING_FLAGS.MSTG_ENABLED && mstgRenderer.hasMSTG) {
      const mstgTrack = mstgRenderer.initMSTG();
      if (mstgTrack) {
        // Wait for track to become live (may take a microtask)
        const isLive = await waitForTrackLive(mstgTrack, 100);
        if (isLive && mstgRenderer.isTrackActive()) {
          stream = new MediaStream([mstgTrack]);
          mstgCaptureStartedRef.current = true;
          logger.info("✅ [MSTG] Using MediaStreamTrackGenerator for precise timestamps");
        } else {
          // Track didn't become live, cleanup and fallback
          mstgRenderer.cleanup();
          stream = domCanvas.captureStream(30);
          mstgCaptureStartedRef.current = false;
          logger.info("⚠️ [MSTG] Track did not become live, falling back to captureStream(30)");
        }
      } else {
        stream = domCanvas.captureStream(30);
        mstgCaptureStartedRef.current = false;
        logger.info("⚠️ [MSTG] Initialization failed, falling back to captureStream(30)");
      }
    } else {
      const isPresentationSlide = (currentScene as any)?.metadata?.sourceType === 'presentation-import';
      if (isPresentationSlide) {
        stream = domCanvas.captureStream(30);
        mstgCaptureStartedRef.current = false;
        canvasVideoTrackRef.current = null;
        logger.info("✅ Using captureStream(30) for presentation slide - forced 30 FPS for static canvas");
      } else {
        stream = domCanvas.captureStream();
        mstgCaptureStartedRef.current = false;
        canvasVideoTrackRef.current = null;
        logger.info("✅ Using captureStream() - capture on canvas change (Vimeo approach)");
      }
    }
    
    canvasStreamRef.current = stream;
    return stream;
  }, [stageRef, mstgRenderer, waitForTrackLive, currentScene]);

  const createCombinedStream = useCallback(async (canvasStream: MediaStream) => {
    const tracks = [...canvasStream.getTracks()];
    
    const audioMixNode = await setupAudioPipeline();
    if (audioMixNode && audioCtxRef.current) {
      const destination = audioCtxRef.current.createMediaStreamDestination();
      audioMixNode.connect(destination);
      tracks.push(...destination.stream.getAudioTracks());
    }

    return new MediaStream(tracks);
  }, [setupAudioPipeline]);

  const getSupportedMimeType = useCallback(() => {
    // Use optimized codec selection from browserCompatibility
    // Prioritizes H.264 for hardware encoding, falls back to VP9/VP8
    // @see Base docs/Vimeo Recording Architecture Analysis.md
    const mimeType = getOptimalMimeType();
    logger.info(`✅ Using MIME type: ${mimeType}`);
    return mimeType;
  }, []);

  const writeChunkToIDB = useCallback(async (videoGuid: string, chunkNo: number, blob: Blob, isLast: boolean = false) => {
    try {
      const sceneMapping = videoGuidToSceneIdRef.current.get(videoGuid);
      
      console.log(`🔍 [DEBUG-IDB] writeChunkToIDB called: videoGuid=${videoGuid}, chunkNo=${chunkNo}, blobSize=${blob.size}, isLast=${isLast}, sceneId=${sceneMapping?.sceneId}`);
      
      const chunkRow = {
        id: `${videoGuid}:${chunkNo}`,
        videoGuid,
        sceneId: sceneMapping?.sceneId,
        chunkNo,
        blob,
        size: blob.size,
        isLast,
        status: 'pending' as const,
        retry: 0,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      await putChunk(chunkRow);
      console.log(`🔍 [DEBUG-IDB] putChunk SUCCESS: videoGuid=${videoGuid}, chunkNo=${chunkNo}`);
      logger.info(`✅ Chunk #${chunkNo} written to IDB for ${videoGuid}${sceneMapping ? ` (scene ${sceneMapping.sceneId})` : ''}`);
      
      // Phase 1.1: Deferred Upload - Only auto-upload for "single" mode
      // For "scene" mode (has scene mapping), buffer chunks in IndexedDB only
      const isSceneMode = !!sceneMapping;
      
      if (isSceneMode) {
        logger.info(`📦 [Deferred Upload] Scene mode detected - buffering chunk #${chunkNo} for ${videoGuid} (upload deferred)`);
      } else {
        logger.info(`📤 [Auto Upload] Single mode - auto-uploading chunk #${chunkNo} for ${videoGuid}`);
        handleChunkUpload(videoGuid, chunkNo, blob.size);
      }
    } catch (error) {
      logger.error(`❌ Failed to write chunk #${chunkNo} to IDB:`, error);
      if (onChunkError) {
        onChunkError(videoGuid, chunkNo);
      }
    }
  }, [onChunkError]);

  const startChunkMonitoring = useCallback((videoGuid: string) => {
    if (chunkIntervalRef.current) {
      clearInterval(chunkIntervalRef.current);
    }

    chunkIntervalRef.current = setInterval(() => {
      const currentVideoGuid = currentVideoGuidRef.current;
      
      if (accumulatedSizeRef.current >= 5 * 1024 * 1024 && chunkAccumulatorRef.current.length > 0 && currentVideoGuid) {
        const combinedBlob = new Blob(chunkAccumulatorRef.current, { type: mediaRecorderRef.current?.mimeType || "video/mp4" });
        const chunkNo = nextChunkNoRef.current++;
        logger.info(`📦 Monitoring: Accumulated chunk #${chunkNo} (${(combinedBlob.size / 1024 / 1024).toFixed(2)} MB)`);
        
        writeChunkToIDB(currentVideoGuid, chunkNo, combinedBlob, false);
        
        chunkAccumulatorRef.current = [];
        accumulatedSizeRef.current = 0;
      }
    }, 15000);
  }, [writeChunkToIDB]);

  const stopChunkMonitoring = useCallback(() => {
    if (chunkIntervalRef.current) {
      clearInterval(chunkIntervalRef.current);
      chunkIntervalRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async (overrideSceneId?: string) => {
    logger.info("🎬 Starting native MediaRecorder recording");

    if (!isRecordingSupportedRef.current) {
      logger.error("Recording not supported in this browser");
      if (onUploadError) {
        onUploadError(
          "browser-check",
          "Recording is not supported in your browser. Please use Chrome, Firefox, or Safari 14+.",
        );
      }
      return;
    }

    try {
      console.log(`🔍 [DEBUG-START] Getting canvas stream, currentScene sourceType=${(currentScene as any)?.metadata?.sourceType}, layoutPreset=${(currentScene as any)?.layoutPreset}`);
      const canvasStream = await getCanvasStream();
      const canvasVideoTracks = canvasStream.getVideoTracks();
      const canvasAudioTracks = canvasStream.getAudioTracks();
      console.log(`🔍 [DEBUG-START] Canvas stream obtained: videoTracks=${canvasVideoTracks.length}, audioTracks=${canvasAudioTracks.length}`, canvasVideoTracks.map(t => ({ id: t.id, label: t.label, readyState: t.readyState, enabled: t.enabled, muted: t.muted, settings: t.getSettings?.() })));
      
      if (onRecordingStart) {
        onRecordingStart();
      }
      
      if (RECORDING_FLAGS.TELEMETRY_ENABLED) {
        frameTimingCollector.startRecording();
      }
      
      console.log(`🔍 [DEBUG-START] Creating combined stream, micStream=${!!micStream}, systemAudioStream=${!!systemAudioStream}`);
      if (micStream) {
        const micTracks = micStream.getAudioTracks();
        console.log(`🔍 [DEBUG-START] micStream tracks:`, micTracks.map(t => ({ id: t.id, label: t.label, readyState: t.readyState, enabled: t.enabled, muted: t.muted })));
      }
      const combinedStream = await createCombinedStream(canvasStream);
      const combinedVideoTracks = combinedStream.getVideoTracks();
      const combinedAudioTracks = combinedStream.getAudioTracks();
      console.log(`🔍 [DEBUG-START] Combined stream: videoTracks=${combinedVideoTracks.length}, audioTracks=${combinedAudioTracks.length}`, { video: combinedVideoTracks.map(t => ({ readyState: t.readyState, enabled: t.enabled })), audio: combinedAudioTracks.map(t => ({ readyState: t.readyState, enabled: t.enabled })) });
      const videoGuid = crypto.randomUUID();
      currentVideoGuidRef.current = videoGuid;
      console.log(`🔍 [DEBUG-START] Generated videoGuid=${videoGuid}`);
      
      // CRITICAL FIX: Use overrideSceneId if provided (for retake), otherwise use ref
      const projectWrapper = activeProjectRef.current;
      // Handle nested project structure: { project: {...}, scenes: [], transitions: [] }
      const project = (projectWrapper as any)?.project || projectWrapper;
      const scene = overrideSceneId 
        ? sceneListRef.current.find(s => s.id === overrideSceneId)
        : currentSceneRef.current;
      
      if (project?.id && scene?.id) {
        videoGuidToSceneIdRef.current.set(videoGuid, {
          sceneId: scene.id,
          projectId: project.id
        });
        logger.info(`🎯 Captured scene mapping: videoGuid=${videoGuid} -> scene=${scene.title} (ID: ${scene.id}), project ID: ${project.id}`);
      }
      
      const mimeType = getSupportedMimeType();
      
      // Use resolution-based bitrates matching Vimeo's configuration
      // @see Base docs/Vimeo Recording Architecture Analysis.md
      const quality = recordingQuality || "1080p";
      const videoBitrate = BITRATES_BY_QUALITY[quality];
      logger.info(`🎥 Using bitrate ${(videoBitrate / 1_000_000).toFixed(1)} Mbps for ${quality}`);
      
      const options: MediaRecorderOptions = {
        videoBitsPerSecond: videoBitrate,
        audioBitsPerSecond: AUDIO_BITRATE,
      };
      
      // Only set mimeType if we have a valid one (guard for unsupported browsers)
      if (mimeType) {
        options.mimeType = mimeType;
      }
      
      // Vimeo-style keyframe interval: 6 seconds for optimal seeking and encoding
      // Only available in Chrome/Edge - other browsers ignore this property
      // @see Base docs/Vimeo Recording Architecture Analysis.md
      if (RECORDING_FLAGS.VIMEO_KEYFRAME_INTERVAL) {
        (options as any).videoKeyFrameIntervalDuration = VIDEO_KEYFRAME_INTERVAL_DURATION;
        logger.info(`🎬 Keyframe interval: ${VIDEO_KEYFRAME_INTERVAL_DURATION}ms (Vimeo-style)`);
      }

      const mediaRecorder = new MediaRecorder(combinedStream, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        console.log(`🔍 [DEBUG-STOP] ondataavailable fired: size=${event.data?.size ?? 0}, type=${event.data?.type}, videoGuid=${videoGuid}, recorderState=${mediaRecorderRef.current?.state}`);
        if (!event.data || event.data.size === 0) {
          console.warn(`🔍 [DEBUG-STOP] ondataavailable SKIPPED - empty data (size=${event.data?.size ?? 'null'})`);
          return;
        }

        chunkAccumulatorRef.current.push(event.data);
        accumulatedSizeRef.current += event.data.size;
        
        console.log(`🔍 [DEBUG-STOP] Accumulator state: items=${chunkAccumulatorRef.current.length}, totalSize=${(accumulatedSizeRef.current / 1024).toFixed(1)}KB, threshold=5MB`);
        
        if (accumulatedSizeRef.current >= 5 * 1024 * 1024) {
          const combinedBlob = new Blob(chunkAccumulatorRef.current, { type: mimeType || "video/mp4" });
          const chunkNo = nextChunkNoRef.current++;
          logger.info(`📦 Accumulated chunk #${chunkNo} (${(combinedBlob.size / 1024 / 1024).toFixed(2)} MB)`);
          
          writeChunkToIDB(videoGuid, chunkNo, combinedBlob, false);
          
          chunkAccumulatorRef.current = [];
          accumulatedSizeRef.current = 0;
        } else {
          logger.info(`📦 Accumulating chunk (${(accumulatedSizeRef.current / 1024 / 1024).toFixed(2)} MB / 5.00 MB)`);
        }
      };

      mediaRecorder.onstop = () => {
        console.log(`🔍 [DEBUG-STOP] MediaRecorder.onstop fired, accumulator: items=${chunkAccumulatorRef.current.length}, size=${(accumulatedSizeRef.current / 1024).toFixed(1)}KB`);
        logger.info("🛑 MediaRecorder stopped");
      };

      mediaRecorder.onerror = (event) => {
        console.error(`🔍 [DEBUG-STOP] MediaRecorder.onerror fired:`, event);
        logger.error("❌ MediaRecorder error:", event);
        if (onUploadError) {
          onUploadError(videoGuid, `MediaRecorder error: ${event}`);
        }
      };

      // Start silent audio loop to prevent browser throttling when tab is hidden
      // This keeps AudioContext active with an inaudible 20kHz tone
      silentAudioLoop.start();
      
      // Vimeo-optimized timeslice: larger chunks reduce CPU interrupts and micro-stutters
      // When VIMEO_TIMESLICE_10S is enabled, use 10s (matches Vimeo exactly)
      // Otherwise use 5s as a balance between responsiveness and performance
      // @see Base docs/Vimeo Recording Architecture Analysis.md
      const timeslice = RECORDING_FLAGS.VIMEO_TIMESLICE_10S ? VIMEO_TIMESLICE : RECOMMENDED_TIMESLICE;
      logger.info(`⏱️ MediaRecorder timeslice: ${timeslice}ms`);
      mediaRecorder.start(timeslice);
      
      startChunkMonitoring(videoGuid);
      
      setIsRecording(true);
      isRecordingRef.current = true;
      recordingTimeRef.current = 0;
      recordingStartTimeRef.current = Date.now();
      isStoppingRef.current = false;
      
      // Update scene's isRecording flag for UI (SlidesRail badge)
      if (currentScene) {
        setSceneList((prev) =>
          prev.map((s) =>
            s.id === currentScene.id ? { ...s, isRecording: true, isPaused: false } : s
          )
        );
      }
      
      logger.info(`✅ Recording started with videoGuid: ${videoGuid}`);
      
    } catch (error) {
      logger.error("Failed to start recording:", error);
      
      // CRITICAL: Cleanup MSTG resources if initialization failed mid-way
      // This prevents stale track/writer state from leaking to next recording
      if (mstgCaptureStartedRef.current) {
        mstgRenderer.cleanup();
        mstgCaptureStartedRef.current = false;
      }
      
      if (onUploadError) {
        onUploadError("start-failed", `Failed to start recording: ${error}`);
      }
    }
  }, [
    stageRef,
    micStream,
    systemAudioStream,
    canvasWidth,
    canvasHeight,
    onUploadError,
    getCanvasStream,
    createCombinedStream,
    getSupportedMimeType,
    startChunkMonitoring,
    writeChunkToIDB,
    currentScene,
    setSceneList,
    onRecordingStart,
    mstgRenderer,
    silentAudioLoop,
  ]);

  const stopRecording = useCallback(async () => {
    console.log(`🔍 [DEBUG-STOP] stopRecording() called, mediaRecorder=${!!mediaRecorderRef.current}, state=${mediaRecorderRef.current?.state}, currentVideoGuid=${currentVideoGuidRef.current}`);
    logger.info("🛑 Stopping MediaRecorder recording");
    
    if (!mediaRecorderRef.current) {
      console.warn("🔍 [DEBUG-STOP] No mediaRecorderRef.current - ABORTING stopRecording");
      logger.warn("No active recording to stop");
      return;
    }

    isStoppingRef.current = true;

    try {
      stopChunkMonitoring();
      
      console.log(`🔍 [DEBUG-STOP] Calling requestData(), recorderState=${mediaRecorderRef.current.state}, accumulator: items=${chunkAccumulatorRef.current.length}, size=${(accumulatedSizeRef.current / 1024).toFixed(1)}KB`);
      mediaRecorderRef.current.requestData();
      
      console.log(`🔍 [DEBUG-STOP] Waiting 500ms after requestData...`);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log(`🔍 [DEBUG-STOP] After 500ms wait, accumulator: items=${chunkAccumulatorRef.current.length}, size=${(accumulatedSizeRef.current / 1024).toFixed(1)}KB, recorderState=${mediaRecorderRef.current?.state}`);
      
      if (mediaRecorderRef.current) {
        console.log(`🔍 [DEBUG-STOP] Calling mediaRecorder.stop()`);
        mediaRecorderRef.current.stop();
      } else {
        console.warn(`🔍 [DEBUG-STOP] mediaRecorderRef.current is null before stop()!`);
      }

      console.log(`🔍 [DEBUG-STOP] Waiting 1000ms after stop...`);
      await new Promise(resolve => setTimeout(resolve, 1000));

      console.log(`🔍 [DEBUG-STOP] After 1000ms wait, accumulator: items=${chunkAccumulatorRef.current.length}, size=${(accumulatedSizeRef.current / 1024).toFixed(1)}KB`);

      const videoGuid = currentVideoGuidRef.current;
      console.log(`🔍 [DEBUG-STOP] videoGuid from ref: ${videoGuid}`);
      if (videoGuid) {
        if (chunkAccumulatorRef.current.length > 0) {
          const finalCombinedBlob = new Blob(chunkAccumulatorRef.current, { type: mediaRecorderRef.current?.mimeType || "video/mp4" });
          const finalChunkNo = nextChunkNoRef.current++;
          console.log(`🔍 [DEBUG-STOP] Writing FINAL CHUNK #${finalChunkNo}, size=${(finalCombinedBlob.size / 1024).toFixed(1)}KB, type=${finalCombinedBlob.type}, videoGuid=${videoGuid}`);
          logger.info(`📦 FINAL CHUNK #${finalChunkNo} (${(finalCombinedBlob.size / 1024 / 1024).toFixed(2)} MB)`);
          
          await writeChunkToIDB(videoGuid, finalChunkNo, finalCombinedBlob, true);
          console.log(`🔍 [DEBUG-STOP] FINAL CHUNK #${finalChunkNo} written to IDB successfully`);
        } else {
          const lastChunkNo = nextChunkNoRef.current++;
          console.warn(`🔍 [DEBUG-STOP] Accumulator EMPTY! Writing empty marker chunk #${lastChunkNo} for videoGuid=${videoGuid}`);
          logger.info(`📦 Creating final marker chunk #${lastChunkNo}`);
          const emptyBlob = new Blob([], { type: mediaRecorderRef.current?.mimeType || "video/mp4" });
          await writeChunkToIDB(videoGuid, lastChunkNo, emptyBlob, true);
          console.log(`🔍 [DEBUG-STOP] Empty marker chunk written to IDB`);
        }
        
        const checkAndSendComplete = async () => {
          const uploadWorker = uploadWorkersRef.current.get(videoGuid);
          if (uploadWorker && uploadInitializedRef.current.has(videoGuid)) {
            logger.info(`📤 Sending complete-upload message for videoGuid: ${videoGuid}`);
            uploadWorker.postMessage({
              type: "complete-upload",
              videoGuid,
              authToken: getUploadConfig().authToken || "",
            });
          } else {
            setTimeout(checkAndSendComplete, 500);
          }
        };
        
        setTimeout(checkAndSendComplete, 1500);
        
        // REMOVED: Old auto-save logic that corrupted duration and conflicted with live upload system
        // Videos now upload LIVE to S3 during recording via upload worker
        // Scene metadata (duration, status) is saved when user clicks "Looks Good" in preview modal
        logger.info('✅ Recording stopped - preview will open automatically (no auto-save)');
      }

      mediaRecorderRef.current = null;
      if (canvasStreamRef.current) {
        canvasStreamRef.current.getTracks().forEach(track => track.stop());
        canvasStreamRef.current = null;
      }
      
      if (audioCleanupRef.current) {
        await audioCleanupRef.current();
        audioCleanupRef.current = null;
      }
      
      // MSTG cleanup: always cleanup first (ensures isTrackActive()=false),
      // then clear the flag (prevents stale state across sessions)
      mstgRenderer.cleanup();
      mstgCaptureStartedRef.current = false;
      
      // Stop silent audio loop that prevents browser throttling
      silentAudioLoop.stop();
      
      // Log frame timing telemetry summary (Phase 3) - gated by TELEMETRY_ENABLED flag
      if (RECORDING_FLAGS.TELEMETRY_ENABLED) {
        frameTimingCollector.logSummary();
        frameTimingCollector.clear();
      }

      chunkAccumulatorRef.current = [];
      accumulatedSizeRef.current = 0;

      const uploadConfig = getUploadConfig();
      if (!isUserAuthenticated() || !uploadConfig.authToken || !uploadConfig.userId) {
        logger.info("User not authenticated, showing download-only overlay");
        
        setTimeout(() => {
          setShowDownloadOnlyOverlay(true);
          if (setIsUploading) {
            setIsUploading(false);
          }
        }, 500);
      }

      setIsRecording(false);
      isRecordingRef.current = false;
      setIsPaused(false);
      
      // Clear scene's isRecording and isPaused flags
      if (currentScene) {
        setSceneList((prev) =>
          prev.map((s) =>
            s.id === currentScene.id ? { ...s, isRecording: false, isPaused: false } : s
          )
        );
      }
      
      logger.info("✅ Recording stopped successfully");
      
    } catch (error) {
      logger.error("Error stopping recording:", error);
    }
  }, [stopChunkMonitoring, writeChunkToIDB, setIsUploading, setSceneList, showRecordingPreview, currentScene, mstgRenderer, silentAudioLoop]);

  const pauseRecording = useCallback(() => {
    if (!mediaRecorderRef.current || !isRecording) {
      logger.warn("Cannot pause - no active recording");
      return;
    }

    logger.info("⏸️ Pausing recording");
    mediaRecorderRef.current.pause();
    setIsPaused(true);
    pauseStartTimeRef.current = Date.now();
    
    // Update scene's isPaused flag for UI (SlidesRail badge)
    if (currentScene) {
      setSceneList((prev) =>
        prev.map((s) =>
          s.id === currentScene.id ? { ...s, isPaused: true } : s
        )
      );
    }
  }, [isRecording, currentScene, setSceneList]);

  const resumeRecording = useCallback(() => {
    if (!mediaRecorderRef.current || !isPaused) {
      logger.warn("Cannot resume - no paused recording");
      return;
    }

    logger.info("▶️ Resuming recording");
    mediaRecorderRef.current.resume();
    setIsPaused(false);
    
    if (pauseStartTimeRef.current) {
      totalPausedDurationRef.current += Date.now() - pauseStartTimeRef.current;
      pauseStartTimeRef.current = null;
    }
    
    // Update scene's isPaused flag for UI (SlidesRail badge)
    if (currentScene) {
      setSceneList((prev) =>
        prev.map((s) =>
          s.id === currentScene.id ? { ...s, isPaused: false } : s
        )
      );
    }
  }, [isPaused, currentScene, setSceneList]);

  const deleteRecording = useCallback(async () => {
    logger.info("🗑️ Deleting recording");
    
    stopChunkMonitoring();
    
    if (mediaRecorderRef.current && isRecording) {
      isStoppingRef.current = true;
      
      try {
        mediaRecorderRef.current.stop();
      } catch (error) {
        logger.error("Error stopping recorder during delete:", error);
      }
    }
    
    const videoGuid = currentVideoGuidRef.current;
    if (videoGuid) {
      await cleanupUploadData(videoGuid);
      // Clean up scene mapping
      videoGuidToSceneIdRef.current.delete(videoGuid);
    }
    
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }
    
    if (canvasStreamRef.current) {
      canvasStreamRef.current.getTracks().forEach(track => track.stop());
      canvasStreamRef.current = null;
    }
    
    if (audioCleanupRef.current) {
      try {
        await audioCleanupRef.current();
      } catch (error) {
        logger.error("Error cleaning up audio:", error);
      }
      audioCleanupRef.current = null;
    }
    
    setIsRecording(false);
    isRecordingRef.current = false;
    setIsPaused(false);
    recordingTimeRef.current = 0;
    currentVideoGuidRef.current = null;
    isStoppingRef.current = false;
    pauseStartTimeRef.current = null;
    totalPausedDurationRef.current = 0;
    nextChunkNoRef.current = 1;
    chunkAccumulatorRef.current = [];
    accumulatedSizeRef.current = 0;
    
    setShowDownloadOnlyOverlay(false);
    
    previousMicStreamRef.current = null;
    
    // Clear scene's isRecording and isPaused flags when deleting recording
    if (currentScene) {
      setSceneList((prev) =>
        prev.map((s) =>
          s.id === currentScene.id ? { ...s, isRecording: false, isPaused: false } : s
        )
      );
    }
    
    logger.info("✅ Recording deleted and cleaned up");
  }, [isRecording, stopChunkMonitoring, currentScene, setSceneList]);

  const createUploadWorkerMessageHandler = useCallback(
    (videoGuid: string) => async (e: MessageEvent) => {
      const {
        type,
        videoGuid: msgVideoGuid,
        chunkNo: msgChunkNo,
        error,
        uploadId,
        key,
        force,
        videoId,
        thumbnailUrl,
      } = e.data;

      console.log(`📬 [createUploadWorkerMessageHandler] RECEIVED message from worker:`, {
        type,
        videoGuid: msgVideoGuid,
        hasKey: !!key,
        hasUploadId: !!uploadId
      });

      switch (type) {
        case "upload-init-complete":
          console.log(`✅✅✅ [upload-init-complete] Message received!`, {
            msgVideoGuid,
            uploadId,
            key,
            force
          });
          logger.info(`📤 Upload initialized for videoGuid: ${msgVideoGuid}`);
          uploadInitializedRef.current.add(msgVideoGuid);
          uploadInitializingRef.current.delete(msgVideoGuid);
          activeUploadsRef.current.add(msgVideoGuid);

          if (force) {
            logger.info(
              `📤 Force mode: Processing all stuck chunks for ${msgVideoGuid}`
            );
            try {
              const areAllDone = await areAllChunksDone(videoGuid);
              const uploadConfig = getUploadConfig();
              const worker = uploadWorkersRef.current.get(videoGuid);
              if (areAllDone && worker && uploadConfig.authToken) {
                worker?.postMessage({
                  type: "complete-upload",
                  videoGuid,
                  authToken: uploadConfig.authToken,
                  userId: uploadConfig.userId,
                  isRecording: isRecordingRef?.current,
                });
              }
              const stuckChunks = await db.chunks
                .where("videoGuid")
                .equals(msgVideoGuid)
                .and((chunk) =>
                  ["uploading", "pending", "error"].includes(chunk.status)
                )
                .toArray();

              if (stuckChunks.length > 0) {
                logger.info(
                  `📤 Found ${stuckChunks.length} stuck chunks to force upload for ${msgVideoGuid}`
                );
                for (const chunk of stuckChunks) {
                  handleChunkUpload(msgVideoGuid, chunk.chunkNo, chunk.size);
                }
              } else {
                logger.info(`📤 No stuck chunks found for ${msgVideoGuid}`);
              }
            } catch (error) {
              logger.error(
                `Error processing stuck chunks in force mode:`,
                error
              );
            }
            break;
          }

          const pendingChunks =
            pendingChunksRef.current.get(msgVideoGuid) || [];
          console.log(`🔍 [upload-init-complete] Checking pending chunks:`, {
            msgVideoGuid,
            pendingChunksCount: pendingChunks.length,
            pendingChunks: pendingChunks.map(c => ({ chunkNo: c.chunkNo, size: c.size }))
          });
          
          if (pendingChunks.length > 0) {
            console.log(`📤 [upload-init-complete] Processing ${pendingChunks.length} pending chunks...`);
            logger.info(
              `📤 Processing ${pendingChunks.length} pending chunks for ${msgVideoGuid}`,
            );
            for (const pendingChunk of pendingChunks) {
              console.log(`🔄 [upload-init-complete] Calling handleChunkUpload for chunk #${pendingChunk.chunkNo}`);
              handleChunkUpload(
                msgVideoGuid,
                pendingChunk.chunkNo,
                pendingChunk.size,
              );
            }
            pendingChunksRef.current.delete(msgVideoGuid);
            console.log(`✅ [upload-init-complete] All pending chunks queued for upload`);
          } else {
            console.log(`⚠️ [upload-init-complete] NO pending chunks found for ${msgVideoGuid}`);
          }
          break;

        case "chunk-uploaded":
          logger.info(
            `📤 Chunk #${msgChunkNo} uploaded for videoGuid: ${msgVideoGuid}`,
          );
          break;

        case "upload-complete":
          logger.info(`✅ Upload completed for videoGuid: ${msgVideoGuid}`);
          
          // CRITICAL FIX: Save S3 key to storage so preview modal can construct videoUrl
          if (key) {
            s3KeyStorage.set(msgVideoGuid, key);
            logger.info(`🔑 S3 key saved to storage for ${msgVideoGuid}: ${key.substring(0, 60)}...`);
          } else {
            logger.error(`❌ No S3 key in upload-complete message for ${msgVideoGuid}`);
          }
          
          // BACKEND THUMBNAIL GENERATION: Save thumbnail URL from backend
          if (thumbnailUrl) {
            const { thumbnailStorage } = await import('../services/thumbnailStorage');
            thumbnailStorage.set(msgVideoGuid, thumbnailUrl);
            logger.info(`🖼️ Thumbnail URL saved to storage for ${msgVideoGuid}: ${thumbnailUrl.substring(0, 60)}...`);
          } else {
            logger.warn(`⚠️ No thumbnail URL in upload-complete message for ${msgVideoGuid} (backend generation may have failed)`);
          }
          
          uploadInitializedRef.current.delete(msgVideoGuid);
          uploadInitializingRef.current.delete(msgVideoGuid);
          pendingChunksRef.current.delete(msgVideoGuid);
          activeUploadsRef.current.delete(msgVideoGuid);
          // Clean up scene mapping after successful upload
          videoGuidToSceneIdRef.current.delete(msgVideoGuid);
          if (currentVideoGuidRef.current === msgVideoGuid) {
            currentVideoGuidRef.current = null;
          }
          if (setIsUploading) {
            setIsUploading(false);
          }
          
          // Mark onboarding task: user recorded their first video (works for both blank and project modes)
          onboardingService.markRecordFirstVideo().catch(() => {});
          
          if (onUploadComplete) {
            if (videoId) {
              onUploadComplete({ videoId, videoGuid: msgVideoGuid });
              nextChunkNoRef.current = 1;
            }
          }
          break;

        case "upload-error":
          let errorMessage = "Unknown upload error";
          try {
            if (error !== undefined && error !== null) {
              errorMessage = error?.toString?.() || String(error) || "Unknown upload error";
            }
          } catch (err) {
            errorMessage = "Error processing upload error";
          }
          logger.error(
            `❌ Upload error for videoGuid: ${msgVideoGuid}, chunk: ${msgChunkNo || 'unknown'}`,
            errorMessage,
          );
          if (onChunkError && msgChunkNo) {
            onChunkError(msgVideoGuid, msgChunkNo);
          }
          break;

        case "upload-init-error":
          logger.error(
            `❌ Upload initialization error for videoGuid: ${msgVideoGuid}`,
            error,
          );
          if (setIsUploading) {
            setIsUploading(false);
          }
          uploadInitializingRef.current.delete(msgVideoGuid);
          uploadInitErrorsRef.current.set(
            msgVideoGuid,
            error || "Upload initialization failed",
          );
          if (onUploadError) {
            currentVideoGuidRef.current = msgVideoGuid;
            onUploadError(
              msgVideoGuid,
              error || "Upload initialization failed",
            );
          }
          break;
      }
    },
    [setIsUploading, onUploadComplete, onUploadError, onChunkError],
  );

  const handleChunkUpload = useCallback(
    async (videoGuid: string, chunkNo: number, size: number) => {
      console.log(`📤 [handleChunkUpload] Called for chunk #${chunkNo}:`, {
        videoGuid: videoGuid.substring(0, 20) + '...',
        chunkNo,
        size,
        hasActiveProject: !!activeProjectRef.current,
        projectId: activeProjectRef.current?.id,
        sceneId: currentSceneRef.current?.id
      });
      
      // DETAILED AUTH DEBUGGING
      const Cookies = require("js-cookie");
      const allCookies = Cookies.get();
      const ssoToken = Cookies.get("SPAExtensionSSO");
      
      console.log(`🍪 [handleChunkUpload] Cookie debug:`, {
        hasSPAExtensionSSO: !!ssoToken,
        ssoTokenLength: ssoToken?.length || 0,
        allCookieNames: Object.keys(allCookies),
        allCookieCount: Object.keys(allCookies).length
      });
      
      const uploadConfig = getUploadConfig();
      const isAuth = isUserAuthenticated();
      
      console.log(`🔐 [handleChunkUpload] Auth check:`, {
        isAuthenticated: isAuth,
        hasAuthToken: !!uploadConfig.authToken,
        hasUserId: !!uploadConfig.userId,
        authTokenLength: uploadConfig.authToken?.length || 0,
        userId: uploadConfig.userId || 'NONE'
      });
      
      // CRITICAL FIX: Only require authToken, not userId
      // The backend can extract user info from the JWT token
      if (
        !isAuth ||
        !uploadConfig.authToken
      ) {
        console.warn(`❌ [handleChunkUpload] SKIPPING upload - auth failed for chunk #${chunkNo}`, {
          isAuth,
          hasAuthToken: !!uploadConfig.authToken
        });
        logger.info(
          "Skipping upload - user not authenticated or missing auth token",
        );
        return;
      }
      
      // Use userId from config if available, otherwise use 'authenticated-user' placeholder
      // The backend will extract the real user ID from the JWT token
      const effectiveUserId = uploadConfig.userId || 'authenticated-user';

      console.log(`✅ [handleChunkUpload] Auth passed, proceeding...`);

      if (abortedVideoGuidsRef.current.has(videoGuid)) {
        console.log(`⏭️ [handleChunkUpload] Aborted videoGuid detected, skipping chunk #${chunkNo}`);
        logger.info(
          `📤 Ignoring chunk #${chunkNo} for aborted videoGuid: ${videoGuid}`,
        );
        return;
      }
      
      console.log(`🎯 [handleChunkUpload] Starting upload logic for chunk #${chunkNo}`);
      
      if (setIsUploading) {
        setIsUploading(true);
      }
      
      try {
        if (uploadInitializedRef.current.has(videoGuid)) {
          console.log(`📤 [handleChunkUpload] Upload already initialized, uploading chunk #${chunkNo}`);
          await uploadChunk(videoGuid, chunkNo);
          return;
        }

        if (uploadInitializingRef.current.has(videoGuid)) {
          console.log(`⏳ [handleChunkUpload] Upload initializing, adding chunk #${chunkNo} to pending queue`);
          const pendingChunks = pendingChunksRef.current.get(videoGuid) || [];
          pendingChunks.push({ chunkNo, size });
          pendingChunksRef.current.set(videoGuid, pendingChunks);
          logger.info(
            `📤 Added chunk #${chunkNo} to pending queue for ${videoGuid}`,
          );
          return;
        }

        console.log(`🆕 [handleChunkUpload] Initializing NEW upload worker for ${videoGuid}`);
        uploadInitializingRef.current.add(videoGuid);
        currentVideoGuidRef.current = videoGuid;

        const uploadWorker = new UploadWorker();
        uploadWorkersRef.current.set(videoGuid, uploadWorker);
        uploadWorker.onmessage = createUploadWorkerMessageHandler(videoGuid);

        // CRITICAL FIX: Use captured sceneId from recording start, not current ref
        const sceneMapping = videoGuidToSceneIdRef.current.get(videoGuid);
        const isCloudProject = sceneMapping && !isNaN(Number(sceneMapping.projectId));
        
        console.log(`📨 [handleChunkUpload] Sending init-upload message to worker`, {
          videoGuid,
          hasAuthToken: !!uploadConfig.authToken,
          effectiveUserId,
          isCloudProject,
          projectId: sceneMapping?.projectId,
          sceneId: sceneMapping?.sceneId,
          mappingFound: !!sceneMapping
        });
        
        logger.info(`🎯 [handleChunkUpload] Upload initialization for videoGuid: ${videoGuid}, sceneId: ${sceneMapping?.sceneId || 'NOT FOUND'}`);
        
        // Use mp4 for blank mode (single recording), webm for cloud projects (scenes)
        const fileExtension = isCloudProject ? "webm" : "mp4";
        
        // Blank mode uses Dadan API via proxy, cloud projects use local S3 backend
        
        uploadWorker.postMessage({
          type: "init-upload",
          videoGuid,
          fileName: generateRecordingFilename(videoGuid, fileExtension),
          authToken: uploadConfig.authToken,
          userId: effectiveUserId,
          totalChunks: 0,
          // Include project/scene context for cloud projects - use captured mapping
          projectId: isCloudProject ? Number(sceneMapping.projectId) : undefined,
          sceneId: isCloudProject ? Number(sceneMapping.sceneId) : undefined,
          // Blank mode: use Dadan API for uploads (via proxy), cloud projects: use local S3 backend
          useDadanApi: !isCloudProject,
        });

        const pendingChunks = pendingChunksRef.current.get(videoGuid) || [];
        pendingChunks.push({ chunkNo, size });
        pendingChunksRef.current.set(videoGuid, pendingChunks);
      } catch (error) {
        logger.error("Error handling chunk upload:", error);
        uploadInitializingRef.current.delete(videoGuid);
      }
    },
    [createUploadWorkerMessageHandler, setIsUploading],
  );

  const uploadChunk = useCallback(
    async (videoGuid: string, chunkNo: number) => {
      console.log(`🚀 [uploadChunk] START for chunk #${chunkNo}, videoGuid: ${videoGuid}`);
      
      if (abortedVideoGuidsRef.current.has(videoGuid)) {
        console.log(`⏭️ [uploadChunk] Aborted videoGuid, skipping chunk #${chunkNo}`);
        logger.info(
          `📤 Ignoring chunk upload #${chunkNo} for aborted videoGuid: ${videoGuid}`,
        );
        return;
      }

      const uploadWorker = uploadWorkersRef.current.get(videoGuid);
      const uploadConfig = getUploadConfig();
      
      console.log(`🔍 [uploadChunk] Worker check:`, {
        hasWorker: !!uploadWorker,
        hasAuthToken: !!uploadConfig.authToken,
        authTokenLength: uploadConfig.authToken?.length || 0
      });
      
      if (!uploadWorker || !uploadConfig.authToken) {
        console.error(`❌ [uploadChunk] FAILED - Missing worker or auth token!`, {
          hasWorker: !!uploadWorker,
          hasAuthToken: !!uploadConfig.authToken
        });
        return;
      }

      console.log(`✅ [uploadChunk] Worker and auth validated, fetching chunk from IDB...`);
      console.log(`🔍 [uploadChunk] About to query IDB with key: ${videoGuid}:${chunkNo}`);
      
      try {
        let chunk;
        try {
          chunk = await db.chunks.get(`${videoGuid}:${chunkNo}`);
          console.log(`✅ [uploadChunk] IDB query completed successfully`);
        } catch (idbError) {
          console.error(`❌ [uploadChunk] IDB query FAILED:`, idbError);
          throw idbError;
        }
        
        console.log(`🔍 [uploadChunk] Chunk lookup result:`, {
          found: !!chunk,
          chunkKey: `${videoGuid}:${chunkNo}`,
          chunkSize: chunk?.blob?.size || 0,
          isLast: chunk?.isLast,
          chunkStatus: chunk?.status,
          hasBlob: !!chunk?.blob
        });
        
        if (!chunk) {
          console.error(`❌ [uploadChunk] Chunk NOT found in IDB: ${videoGuid}:${chunkNo}`);
          logger.error(`Chunk not found for upload: ${videoGuid}:${chunkNo}`);
          return;
        }

        let chunkBlob: Blob;
        if (chunk.blob instanceof Blob) {
          chunkBlob = chunk.blob;
        } else {
          chunkBlob = new Blob([chunk.blob as ArrayBuffer], { type: "video/mp4" });
        }

        const isCurrentlyRecording =
          isRecordingRef.current && currentVideoGuidRef.current === videoGuid;

        console.log(`📤 [uploadChunk] Sending upload-chunk message to worker:`, {
          videoGuid,
          chunkNo,
          chunkSize: chunkBlob.size,
          isLast: chunk.isLast,
          isRecording: isCurrentlyRecording
        });
        
        uploadWorker.postMessage({
          type: "upload-chunk",
          videoGuid,
          chunkNo,
          chunkData: chunkBlob,
          isLast: chunk.isLast,
          authToken: uploadConfig.authToken,
          isRecording: isCurrentlyRecording,
        });
        
        console.log(`✅ [uploadChunk] Message sent successfully to worker`);
      } catch (error) {
        console.error(`❌ [uploadChunk] ERROR:`, error);
        logger.error("Error uploading chunk:", error);
      }
    },
    [],
  );

  const cleanupUploadData = useCallback(async (videoGuid: string) => {
    abortedVideoGuidsRef.current.add(videoGuid);

    const uploadWorker = uploadWorkersRef.current.get(videoGuid);
    if (uploadWorker) {
      try {
        const uploadConfig = getUploadConfig();
        uploadWorker.postMessage({
          type: "abort-upload",
          videoGuid,
          authToken: uploadConfig.authToken || "",
        });
        
        setTimeout(() => {
          try {
            uploadWorker.terminate();
            logger.info(`Terminated upload worker for ${videoGuid}`);
          } catch (error) {
            logger.error(`Error terminating worker for ${videoGuid}:`, error);
          }
        }, 100);
      } catch (error) {
        logger.error(`Error aborting upload for ${videoGuid}:`, error);
      }
    }

    uploadWorkersRef.current.delete(videoGuid);
    uploadInitializedRef.current.delete(videoGuid);
    uploadInitializingRef.current.delete(videoGuid);
    pendingChunksRef.current.delete(videoGuid);
    activeUploadsRef.current.delete(videoGuid);
    uploadInitErrorsRef.current.delete(videoGuid);

    try {
      await deleteChunks(videoGuid);
      await deleteUpload(videoGuid);
      logger.info(`Cleaned up IDB data for videoGuid: ${videoGuid}`);
      
      nextChunkNoRef.current = 1;
    } catch (error) {
      logger.error(`Error cleaning up IDB data for ${videoGuid}:`, error);
    }

    if (currentVideoGuidRef.current === videoGuid) {
      currentVideoGuidRef.current = null;
    }
  }, []);

  const retryUpload = useCallback(
    async (videoGuid: string, force: boolean = false) => {
      const uploadConfig = getUploadConfig();
      const userAuth = isUserAuthenticated();
      if (
        !userAuth ||
        !uploadConfig.authToken ||
        !uploadConfig.userId
      ) {
        logger.error(
          "Missing upload configuration or user not authenticated for retry",
        );
        return;
      }

      if (abortedVideoGuidsRef.current.has(videoGuid)) {
        logger.warn(
          `Skipping retry for videoGuid: ${videoGuid} - was recently aborted`,
        );
        return;
      }

      try {
        let uploadWorker = uploadWorkersRef.current.get(videoGuid);
        if (!uploadWorker) {
          const newWorker = new UploadWorker();
          uploadWorkersRef.current.set(videoGuid, newWorker);
          newWorker.onmessage = createUploadWorkerMessageHandler(videoGuid);
          uploadWorker = newWorker;
        }

        const uploadConfig = getUploadConfig();
        uploadWorker!.postMessage({
          type: "retry-init-upload",
          videoGuid,
          authToken: uploadConfig.authToken,
          userId: uploadConfig.userId,
          force,
        });

        logger.info(
          `Retry-init-upload message sent for videoGuid: ${videoGuid}`,
        );
      } catch (error) {
        logger.error("Error sending retry message:", error);
      }
    },
    [isUserAuthenticated, createUploadWorkerMessageHandler],
  );

  useEffect(() => {
    if (isRecordingRef.current && micStream !== previousMicStreamRef.current) {
      previousMicStreamRef.current = micStream;
      updateAudioPipeline();
    }
  }, [micStream, updateAudioPipeline]);

  useEffect(() => {
    return () => {
      uploadWorkersRef.current.forEach((worker, videoGuid) => {
        try {
          worker.terminate();
          logger.info(
            `📤 Terminated upload worker for ${videoGuid} on unmount`,
          );
        } catch (error) {
          logger.error(`Error terminating worker for ${videoGuid}:`, error);
        }
      });
      uploadWorkersRef.current.clear();

      stopChunkMonitoring();

      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stop();
      }

      if (audioCleanupRef.current) {
        audioCleanupRef.current();
      }
    };
  }, [stopChunkMonitoring]);

  const getSceneIdForVideoGuid = useCallback((videoGuid: string): string | undefined => {
    const mapping = videoGuidToSceneIdRef.current.get(videoGuid);
    return mapping?.sceneId;
  }, []);
  
  // Request a new frame from the canvas capture stream
  // Called after each direct render to ensure hidden-tab recording captures frames
  const requestCanvasFrame = useCallback(() => {
    const track = canvasVideoTrackRef.current;
    if (track && track.readyState === 'live') {
      try {
        track.requestFrame();
        return true;
      } catch (error) {
        // Track may have ended
        return false;
      }
    }
    return false;
  }, []);

  return {
    isRecording,
    isPaused,
    getRecordingTime: () => recordingTimeRef.current,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    deleteRecording,
    retryUpload,
    currentVideoGuid: currentVideoGuidRef.current,
    activeUploads: activeUploadsRef.current,
    uploadInitializing: uploadInitializingRef.current,
    pendingChunks: pendingChunksRef.current,
    showDownloadOnlyOverlay,
    setShowDownloadOnlyOverlay,
    getSceneIdForVideoGuid,
    mstgWriteFrame: RECORDING_FLAGS.MSTG_ENABLED ? mstgRenderer.writeFrame : null,
    hasMSTG: mstgRenderer.hasMSTG,
    isMSTGActive: mstgRenderer.isTrackActive,
    requestCanvasFrame,
  };
};
